import React, { Suspense, useState } from 'react';
import styled, { keyframes } from "styled-components";
import { LuArrowLeft } from "react-icons/lu";

import brk from "../util/breakpoints";
import { orbitalGlow } from "../util/glow";
import { prefersReducedMotion } from "../util/motion";

// comps
import Container from "../components/Container";
import Button from "../components/Button";
import SceneBoundary from "../components/3JS/SceneBoundary";

// Loaded separately so three.js stays out of the main bundle
const NotFoundScene = React.lazy(() => import("../components/3JS/NotFoundScene"));


const drift = keyframes`
    0%, 100% { transform: translate(475px, 490px) rotate(0deg); }
    50% { transform: translate(469px, 478px) rotate(-5deg); }
`;

const twinkle = keyframes`
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
`;

const fadeUp = keyframes`
    from { opacity: 0; transform: translateY(18px); }
    to { opacity: 1; transform: translateY(0); }
`;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: 100vh;
    padding-top: var(--navHeight);
    background-color: var(--bg);
    background-image: ${orbitalGlow};
    background-size: 100% 100%;
    overflow: hidden;
`;

const Content = styled.div`
    display: flex;
    align-items: center;
    gap: 60px;
    padding: 48px 0;
    @media ${brk.device.pc} {
        flex-direction: column;
        align-items: flex-start;
        gap: 32px;
    }
`;

const ErrorCopy = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 24px;
    flex-shrink: 0;
    width: 560px;
    max-width: 100%;
    animation: ${fadeUp} 0.6s ease-out both;
`;

const ErrorMarker = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    font-family: var(--fontMono);
    font-size: 10px;
    white-space: nowrap;
    span {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: var(--purple);
    }
    b {
        color: var(--purpleText);
        font-weight: 600;
        text-transform: uppercase;
    }
    i {
        width: 48px;
        height: 1px;
        background: var(--borderStrong);
    }
    p {
        color: var(--textDim);
    }
`;

const ErrorHeading = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    font-family: var(--fontHeading);
    font-weight: 700;
    h1 {
        color: var(--lime);
        font-size: clamp(88px, 16vw, 128px);
        line-height: 0.82;
    }
    h2 {
        color: var(--text);
        font-size: clamp(30px, 6vw, 44px);
        line-height: 1.04;
    }
`;

const Description = styled.p`
    max-width: 500px;
    color: var(--textMuted);
    font-family: var(--fontBody);
    font-size: 17px;
    line-height: 1.58;
`;

const Illustration = styled.div`
    position: relative;
    flex: 1;
    min-width: 0;
    max-width: 676px;
    aspect-ratio: 676 / 690;
    > svg {
        display: block;
        width: 100%;
        height: 100%;
        overflow: visible;
        visibility: ${(props) => (props.$sceneReady ? "hidden" : "visible")};
    }
    .star {
        animation: ${twinkle} 4s ease-in-out infinite;
    }
    .probe {
        animation: ${drift} 9s ease-in-out infinite;
    }
    @media ${brk.device.pc} {
        flex: none;
        width: 100%;
        max-width: 460px;
        align-self: center;
    }
`;

const SceneBox = styled.div`
    position: absolute;
    inset: 0;
    opacity: ${(props) => (props.$on ? 1 : 0)};
    transition: opacity 0.6s;
`;

const IllustrationLabel = styled.div`
    position: absolute;
    left: 9.17%;
    top: 89.86%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 79.88%;
    padding: 12px 16px;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: var(--surface);
    font-family: var(--fontMono);
    font-size: 9px;
    white-space: nowrap;
    p {
        color: var(--textDim);
    }
    div {
        display: flex;
        align-items: center;
        gap: 8px;
        color: var(--purpleText);
    }
    span {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: var(--purple);
    }
`;

const PageFooter = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 68px;
    padding: 12px 0;
    border-top: 1px solid var(--border);
    font-family: var(--fontMono);
    font-size: 9px;
    text-transform: uppercase;
    p {
        color: var(--textDim);
    }
    span {
        color: var(--lime);
    }
    @media ${brk.device.tablet} {
        flex-direction: column;
        align-items: flex-start;
        justify-content: center;
    }
`;

const Main = styled(Container)`
    display: flex;
    flex-direction: column;
    justify-content: center;
    flex: 1;
`;

function NotFound() {

    // The 3D scene replaces the static illustration once it's running; the SVG stays as the fallback.
    const [sceneAllowed, setSceneAllowed] = useState(() => !prefersReducedMotion());
    const [sceneReady, setSceneReady] = useState(false);

    return (
        <>
            <Wrapper>
                <Main>
                    <Content>
                        <ErrorCopy>
                            <ErrorMarker>
                                <span />
                                <b>Navigation fault / 404</b>
                                <i />
                                <p>SECTOR UNKNOWN</p>
                            </ErrorMarker>
                            <ErrorHeading>
                                <h1>404</h1>
                                <h2>This level isn’t in the build.</h2>
                            </ErrorHeading>
                            <Description>
                                The route drifted out of bounds—or the page got cut before launch. Your progress is safe. Let’s get you back to known space.
                            </Description>
                            <Button href="/" icon={<LuArrowLeft size={16} />}>
                                Return to base
                            </Button>
                        </ErrorCopy>
                        <Illustration $sceneReady={sceneAllowed && sceneReady}>
                            {   sceneAllowed &&
                                <SceneBox $on={sceneReady}>
                                    <SceneBoundary onError={() => { setSceneAllowed(false); }}>
                                        <Suspense fallback={null}>
                                            <NotFoundScene onReady={() => { setSceneReady(true); }} />
                                        </Suspense>
                                    </SceneBoundary>
                                </SceneBox>
                            }
                            <svg viewBox="0 0 676 690" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <defs>
                                    <linearGradient id="planetFill" x1="260.4" y1="229.2" x2="287.77" y2="448.17" gradientUnits="userSpaceOnUse">
                                        <stop stopColor="#2C2354" />
                                        <stop offset="0.58" stopColor="#15182A" />
                                        <stop offset="1" stopColor="#0A0C13" />
                                    </linearGradient>
                                    <filter id="atmosphereGlow" x="126" y="120" width="420" height="420" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                                        <feOffset />
                                        <feGaussianBlur stdDeviation="30" />
                                        <feComposite in2="hardAlpha" operator="out" />
                                        <feColorMatrix type="matrix" values="0 0 0 0 0.556863 0 0 0 0 0.447059 0 0 0 0 1 0 0 0 0.227451 0" />
                                        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
                                        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
                                    </filter>
                                </defs>

                                {/* Lost probe */}
                                <g className="probe" transform="translate(475 490)">
                                    <line x1="44.0006" y1="17.9651" x2="44.6288" y2="-0.0239365" stroke="#A5ADBC" strokeWidth="2" />
                                    <circle cx="45.5" cy="4.5" r="3.5" fill="#B8F23D" />
                                    <rect x="23.5" y="18.5" width="45" height="33" rx="7.5" fill="#191E2B" stroke="#747D90" />
                                    <rect x="2.5" y="26.5" width="23" height="17" fill="#241D46" stroke="#8E72FF" />
                                    <rect x="66.5" y="26.5" width="23" height="17" fill="#241D46" stroke="#8E72FF" />
                                    <circle cx="46" cy="35" r="4" fill="#B8F23D" />
                                </g>

                                {/* Planet */}
                                <line x1="-175" y1="0" x2="175" y2="0" transform="translate(332.65 311.46) rotate(-14)" stroke="#B8F23D" strokeOpacity="0.156863" strokeWidth="10" strokeLinecap="round" />
                                <line x1="-189.5" y1="0" x2="189.5" y2="0" transform="translate(331.33 293.8) rotate(-14)" stroke="#B8F23D" strokeWidth="3" strokeLinecap="round" />
                                <ellipse cx="370" cy="330" rx="92" ry="126" fill="#080A10" fillOpacity="0.564706" />
                                <circle cx="336" cy="330" r="126" fill="url(#planetFill)" />
                                <circle cx="336" cy="330" r="125.5" stroke="#8E72FF" strokeOpacity="0.4" />
                                <g filter="url(#atmosphereGlow)">
                                    <circle cx="336" cy="330" r="150" fill="#8E72FF" fillOpacity="0.0705882" />
                                </g>

                                {/* Stars */}
                                <circle className="star" cx="321.5" cy="33.5" r="1.5" fill="#747D90" />
                                <circle className="star" cx="622" cy="432" r="2" fill="#B8F23D" style={{ animationDelay: "1.3s" }} />
                                <circle className="star" cx="48" cy="352" r="2" fill="#B9A9FF" style={{ animationDelay: "2.2s" }} />
                                <path className="star" transform="translate(516 101.5) rotate(45) translate(-2.378 -2.5)" d="M2.37764 0L2.93893 1.72746H4.75529L3.28582 2.79509L3.84711 4.52255L2.37764 3.45492L0.908179 4.52255L1.46947 2.79509L0 1.72746H1.81636L2.37764 0Z" fill="#F2F4EA" style={{ animationDelay: "0.7s" }} />
                                <path className="star" transform="translate(126 564.95) rotate(45) translate(-3.329 -3.5)" d="M3.3287 0L4.1145 2.41844H6.6574L4.60015 3.91312L5.38595 6.33156L3.3287 4.83689L1.27145 6.33156L2.05725 3.91312L0 2.41844H2.5429L3.3287 0Z" fill="#747D90" style={{ animationDelay: "1.9s" }} />
                                <path className="star" transform="translate(592 225.66) rotate(45) translate(-3.804 -4)" d="M3.80423 0L4.70229 2.76393L7.60846 2.76393L5.25731 4.47214L6.15537 7.23607L3.80423 5.52787L1.45309 7.23607L2.35114 4.47214L0 2.76393L2.90617 2.76393L3.80423 0Z" fill="#8E72FF" style={{ animationDelay: "2.8s" }} />
                                <path className="star" transform="translate(80 147.78) rotate(45) translate(-5.231 -5.5)" d="M5.23081 0L6.46564 3.80041H10.4616L7.22881 6.14919L8.46363 9.9496L5.23081 7.60082L1.99799 9.9496L3.23282 6.14919L0 3.80041H3.99599L5.23081 0Z" fill="#B8F23D" style={{ animationDelay: "0.4s" }} />

                                {/* Orbits */}
                                <circle cx="328" cy="336" r="262.5" stroke="#747D90" strokeOpacity="0.7" strokeDasharray="4 12" />
                                <circle cx="328" cy="334" r="207.5" stroke="#B8F23D" strokeOpacity="0.164706" />
                                <circle cx="328" cy="334" r="309.5" stroke="#8E72FF" strokeOpacity="0.219608" />
                            </svg>
                            <IllustrationLabel>
                                <p>LAT ??? · LON ???</p>
                                <div>
                                    <span />
                                    TELEMETRY LOST
                                </div>
                            </IllustrationLabel>
                        </Illustration>
                    </Content>
                </Main>
                <Container>
                    <PageFooter>
                        <p>{`© ${new Date().getFullYear()} James Spears · Error handled with care`}</p>
                        <span>Core systems still nominal</span>
                    </PageFooter>
                </Container>
            </Wrapper>
        </>
    );
};

export default NotFound;
